// usePermission — client-side mirror of server's userHasPermission helper
// (server/middleware/auth.js). Use it for UI gating where the server gates
// the API: hide buttons, hide columns, branch render paths.
//
// Semantics MUST match the server. If they drift, the UI will show or hide
// things that the API allows or rejects, which produces "ghost button"
// bugs (button visible, click returns 403). Keep this thin file in sync
// with userHasPermission in middleware/auth.js.

import { useAuth } from '../App';

/**
 * useCan(resource, action) -> boolean
 *
 * Returns true if the current user has `permissions[resource]` containing
 * `action` (or 'manage_all' as a wildcard). Admin always returns true.
 *
 * Returns false when:
 *   - no user (not logged in / loading)
 *   - permissions object is missing (legacy session before /me hydrates)
 *   - the resource isn't in permissions
 *   - the action isn't in permissions[resource] and 'manage_all' isn't either
 *
 * Marker actions ('manage_team', 'read_self', 'read_all', 'read_sensitive')
 * are NOT wildcards — only 'manage_all' is. Match server semantics exactly.
 */
export function useCan(resource, action) {
  const { user } = useAuth();

  if (!user) return false;
  // Admin shortcut — same as server-side `if (role === 'admin') return next()`.
  if (user.role === 'admin') return true;

  const perms = user.permissions;
  if (!perms || !perms[resource]) return false;

  return (
    perms[resource].includes(action) ||
    perms[resource].includes('manage_all')
  );
}

/**
 * Whether useCan's answers are final yet. Before /me hydrates, useCan answers
 * false for anything but an admin — so a screen acting ONCE on a permission
 * (Jobs' ?new=1) must wait for this, or it acts on "no" and the moment is
 * gone. The same two conditions as useCan's early returns, kept here so the
 * two cannot drift.
 */
export function usePermissionsKnown() {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === 'admin') return true;
  return !!user.permissions;
}

/**
 * useRole() -> string | null
 *
 * Convenience accessor for the current user's role. Use sparingly —
 * permission checks should go through useCan, not useRole, so that
 * per-user extras (Phase 2D) take effect. Reach for useRole only when
 * you genuinely need to branch on the role label itself (e.g., showing
 * different welcome copy for a worker vs. an admin).
 */
export function useRole() {
  const { user } = useAuth();
  return user?.role || null;
}
